import { Box, Container, Typography, Skeleton, Grid } from "@mui/material";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";

import { Route as NewExchangeRoute } from "./new";
import { allOptinsQueryOptions, useOptins } from "../../../api/optins";
import { NewOptinButton } from "../../../components/NewOptinDialog/NewOptinButton";
import type { Optin } from "../../../types/optins";

import { installedConnectorsQueryOptions } from "@/api/connectors/index";
import { ExchangeStepsPreview } from "@/components/ExchangeStepsPreview/ExchangeStepsPreview";
import { filterExchangeTemplates } from "@/components/NewOptinDialog/templateMetadata";
import { OptinCard } from "@/components/OptinCard/OptinCard";
import { ExchangesPageHeader } from "@/features/Exchanges/ExchangesPageHeader";
import { ExchangesPreviewButton } from "@/features/Exchanges/ExchangesPreviewButton";
import { ExchangesSearchFilters as SearchFilters } from "@/features/Exchanges/ExchangesSearchFilters";
import {
  ExchangesFiltersSchema,
  useUpdateExchangesFilter,
  type ExchangesFilters,
} from "@/features/Exchanges/useUpdateExchangesFilter";
import { AddIcon } from "@/icons";
// import { useAuth } from "../../../contexts/AuthContext";

const centerContentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "50vh",
  gap: 3,
};

export const Route = createFileRoute("/_auth/exchanges/$siteId")({
  validateSearch: (raw): ExchangesFilters => ExchangesFiltersSchema.parse(raw),
  loader: ({ context: { queryClient }, params: { siteId } }) => {
    // Warm the installed-connector list for the Targeting tab's Session
    // control, which stays hidden until that query settles. Covers this page,
    // /new and /$id/edit — the editor is a child of this layout route, so all
    // three places the configure dialog opens from are served by one call.
    //
    // prefetch, not ensure: nothing on the page needs it, so it must never
    // block navigation.
    void queryClient.prefetchQuery(installedConnectorsQueryOptions(siteId));
    return queryClient.ensureQueryData(allOptinsQueryOptions(siteId));
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const matches = useMatches();
  const isNewExchangePage = matches.some(
    (match) => match.routeId === NewExchangeRoute.id,
  );

  const { data: optins = [], isLoading } = useOptins(siteId || "", {
    enabled: !!siteId && !isNewExchangePage,
  });

  if (isNewExchangePage) {
    return <Outlet />;
  }

  if (!siteId) {
    return (
      <Box sx={centerContentStyle}>
        <Typography variant="h6">Please select a site first</Typography>
      </Box>
    );
  }

  return (
    <>
      <ExchangesPageHeader
        breadcrumbs={[
          { label: "Connect", to: "/dragon-tiles/$siteId", params: { siteId } },
          { label: "Exchanges" },
        ]}
      >
        <Box display="flex" gap={4}>
          <ExchangesSearchFilters />
          <ExchangesPreviewButton siteId={siteId} optins={optins} />
          <NewOptinButton
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddIcon />}
            siteId={siteId}
          >
            New exchange
          </NewOptinButton>
        </Box>
      </ExchangesPageHeader>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <ExchangesList optins={optins} siteId={siteId} />
      )}
      <Outlet />
    </>
  );
}

/**
 * Split out from `RouteComponent` so filter changes (which re-render this
 * subtree via `useUpdateExchangesFilter`'s `Route.useSearch()` subscription)
 * don't also re-render the page header/breadcrumbs/new-exchange button.
 */
function ExchangesList({
  optins,
  siteId,
}: {
  optins: Optin[];
  siteId: string;
}) {
  const listedOptins = useFilteredOptins(optins);

  const [selectedOptin, setSelectedOptin] = useState<Optin | null>(null);

  useEffect(() => {
    setSelectedOptin((current) => {
      const stillVisible = current
        ? listedOptins.find((optin) => optin.id === current.id)
        : undefined;
      return stillVisible ?? listedOptins[0] ?? null;
    });
  }, [listedOptins]);

  const hasNoOptins = optins.filter((o) => o.mode !== "noContent").length === 0;

  if (hasNoOptins) {
    return (
      <PageContainer>
        <Grid>
          <Box sx={centerContentStyle}>
            <Typography variant="h6">
              You don't have any exchanges yet
            </Typography>
            <NewOptinButton
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AddIcon />}
              siteId={siteId}
            >
              Create your first exchange
            </NewOptinButton>
          </Box>
        </Grid>
      </PageContainer>
    );
  }

  if (listedOptins.length === 0) {
    return (
      <PageContainer>
        <Box sx={centerContentStyle}>
          <Typography variant="h6" color="text.secondary">
            No exchanges match your filters
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  // Full-bleed, as in the EXC-204 design: the list on the left, and the
  // preview's dark canvas filling the rest of the page to its right and
  // bottom edges.
  return (
    <Grid container sx={{ flex: 1, minHeight: 0 }}>
      <Grid
        component={motion.div}
        size={{ xs: 12, md: 6 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        style={{
          height: "100%",
          minHeight: 0,
          overflow: "auto",
          padding: 16,
        }}
      >
        <AnimatePresence mode="popLayout">
          {listedOptins.map((optin, index) => (
            <motion.div
              key={optin.id}
              onClick={() => setSelectedOptin(optin)}
              style={{
                cursor: "pointer",
                paddingBottom: 8,
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: 20,
                transition: { delay: 0, duration: 0 },
              }}
              transition={{
                duration: 0.3,
                delay: index * 0.15, // Stagger effect: each card animates 0.1s after the previous
                ease: "easeOut",
              }}
            >
              <OptinCard
                optin={optin}
                isSelected={selectedOptin?.id === optin.id}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </Grid>
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          height: "100%",
          minHeight: 0,
          bgcolor: "background.paper-elevation-reversed",
          p: "14px",
        }}
      >
        {selectedOptin && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            sx={{ height: "100%" }}
          >
            <ExchangeStepsPreview optin={selectedOptin} />
          </Box>
        )}
      </Grid>
    </Grid>
  );
}

function PageContainer(props: React.PropsWithChildren) {
  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 5,
        height: "calc(100vh - 200px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {props.children}
    </Container>
  );
}

function useFilteredOptins(optins: Optin[]) {
  const { filters } = useUpdateExchangesFilter(Route);

  return useMemo(
    () =>
      filterExchangeTemplates(optins, filters).sort(
        (a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1), // Put active optins at the top
      ),
    [optins, filters],
  );
}

function ExchangesSearchFilters() {
  const { filters, updateFilter } = useUpdateExchangesFilter(Route);

  return <SearchFilters filters={filters} onFilterChange={updateFilter} />;
}

// Mirrors the loaded page's full-bleed split, so nothing jumps when it lands.
const LoadingSkeleton = () => (
  <Grid container sx={{ flex: 1, minHeight: 0 }}>
    <Grid
      size={{ xs: 12, md: 6 }}
      sx={{
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        p: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {[1, 2, 3, 4].map((index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          width="100%"
          height={160}
          sx={{ borderRadius: 3, flex: "none" }}
        />
      ))}
    </Grid>
    <Grid
      size={{ xs: 12, md: 6 }}
      sx={{
        height: "100%",
        minHeight: 0,
        bgcolor: "background.paper-elevation-reversed",
        p: "14px",
      }}
    />
  </Grid>
);
